import { Flex } from 'common/src/designSystem/components/flex';
import { CampaignState, CampaignType } from 'common/src/generated/types';
import { CampaignInputService, ICreateUpdateCampaignValues } from 'common/src/input/campaignInput';
import { DateTimeService } from 'common/src/services/dateTimeService';
import { useService } from 'common/src/util/dependencies/dependencies';
import { LocaleFormats } from 'common/src/util/luxon';
import { DateTime } from 'luxon';
import * as React from 'react';
import { FormRenderProps } from 'react-final-form';
import { CreateCampaignTextEmail } from '../../campaigns/create/text/createCampaignTextEmail';
import { CreateCampaignTextSms } from '../../campaigns/create/text/createCampaignTextSms';
import { Form } from '../../components/form/form';
import { Button } from '../../designSystem/components/button';
import { RightPanel } from '../../designSystem/components/rightPanel/rightPanel';
import { RightPanelBody } from '../../designSystem/components/rightPanel/rightPanelBody';
import { RightPanelFooter } from '../../designSystem/components/rightPanel/rightPanelFooter';
import { RightPanelHeader } from '../../designSystem/components/rightPanel/rightPanelHeader';
import {
    useCampaignCreateMutation,
    useCreateCampaignInfosQuery
} from '../../generated/graphqlHooks';
import { useHeavent } from '../../hooks/useHeavent';

interface ISendEmailTextRightPanelProps {
    campaignLevel: 'event' | 'organization';
    campaignType: CampaignType;

    onClose(): void;
}

export const SendEmailTextRightPanel = (props: ISendEmailTextRightPanelProps) => {
    const {
        translate,
        params: { organizationId, eventId, userInfoId }
    } = useHeavent();
    const campaignInput = useService(CampaignInputService);
    const dateTimeService = useService(DateTimeService);
    const { mutate: campaignCreate } = useCampaignCreateMutation();
    const { data } = useCreateCampaignInfosQuery({ organizationId, eventId, userInfoId });
    const initialValues = React.useMemo(() => {
        const values = campaignInput.createUpdateCampaignValuesDefault(
            true,
            false,
            data.organization?.event?.emailSender?.id ?? data.organization?.emailSenderId,
            null,
            null
        );
        const name = data.organization?.userInfo?.name || '';
        const date = dateTimeService.toLocaleString(DateTime.now(), LocaleFormats.FullDateTime);

        return {
            ...values,
            campaign: {
                ...values.campaign,
                name:
                    props.campaignType === CampaignType.Sms
                        ? translate('sms_1_le_2_25252', name, date)
                        : translate('e_mail_1_le_21805', name, date),
                campaignType: props.campaignType,
                state: CampaignState.InProgress,
                sendToEveryone: false,
                usersInfosIds: [userInfoId]
            }
        };
    }, [props.campaignType, data.organization]);

    return (
        <Form
            initialValues={initialValues}
            render={(formProps: FormRenderProps<ICreateUpdateCampaignValues>) => (
                <RightPanel size="md" onClose={props.onClose}>
                    <RightPanelHeader>
                        {props.campaignType === CampaignType.Sms
                            ? translate('envoi_d_un_sms_22148')
                            : translate('envoi_d_un_emai_99336')}
                    </RightPanelHeader>

                    <RightPanelBody css={{ padding: '$6' }}>
                        {props.campaignType === CampaignType.Email ? (
                            <CreateCampaignTextEmail
                                campaignType={props.campaignLevel}
                                customDocuments={data.organization?.event?.customDocuments ?? []}
                                emailSenderId={
                                    data.organization?.event?.emailSender?.id ??
                                    data.organization?.emailSenderId
                                }
                                emailsSenders={data.organization?.emailsSenders ?? []}
                                form={formProps.form}
                                values={formProps.values}
                            />
                        ) : (
                            <CreateCampaignTextSms formProps={formProps} />
                        )}
                    </RightPanelBody>

                    <RightPanelFooter>
                        <Flex gap="3" justify="end">
                            <Button color="white" onClick={props.onClose}>
                                {translate('annuler_48254')}
                            </Button>

                            <Button
                                isLoading={formProps.submitting}
                                onClick={formProps.handleSubmit}
                            >
                                {translate('envoyer_26609')}
                            </Button>
                        </Flex>
                    </RightPanelFooter>
                </RightPanel>
            )}
            onSubmit={async (values: ICreateUpdateCampaignValues) => {
                await campaignCreate({
                    organizationId,
                    eventId,
                    campaign: values.campaign
                });

                props.onClose();
            }}
        />
    );
};
